import { CompoundQuestionTemplateData, DeploymentPlace } from "types";

import { humps } from "@civicscience/chops";

export const buildDeploymentPlaceLabel = (place: DeploymentPlace): string =>
  `${place.feedName} (${place.segmentName ?? "Everyone"})`;

const placeKey = (place: DeploymentPlace): string => `${place.feedId}:${place.segmentName ?? ""}`;

/**
 * Dedupes deployment places across a compound question's items. A compound is
 * considered deployed wherever any item is--the serving engine shows it with
 * whichever items are eligible at a given target--so this is a plain union.
 */
export const getUniqueDeploymentPlaces = (placesByItem: DeploymentPlace[][]): DeploymentPlace[] => {
  const seen = new Map<string, DeploymentPlace>();
  placesByItem.flat().forEach((place) => seen.set(placeKey(place), place));
  return Array.from(seen.values());
};

export const isSuccessStatus = (status: number): boolean => status >= 200 && status < 300;

/**
 * Pulls human-readable error messages out of an API error body.
 * Template errors come back as `{ detail: string[] }`; FastAPI request errors as `{ detail: { msg }[] }`.
 */
export const getTemplateErrorMessages = (body: unknown): string[] => {
  const detail = (body as { detail?: unknown } | null)?.detail;

  if (typeof detail === "string") {
    return [detail];
  }
  if (!Array.isArray(detail)) {
    return [];
  }
  return detail
    .map((item) => (typeof item === "string" ? item : (item as { msg?: string } | null)?.msg))
    .filter((message): message is string => !!message);
};

/**
 * Converts a raw (snake_case) validate response body into typed template data for display.
 */
export const toCompoundQuestionTemplateData = (body: unknown): CompoundQuestionTemplateData =>
  humps.camelizeKeys(body) as CompoundQuestionTemplateData;
